import React, { FunctionComponent, useCallback, useState } from 'react';
import { SquarePlus } from 'lucide-react';
import { CheckupTag, PetAndClinicInfo } from '@companion-professional/dctypes';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  QueryLoaderWrapper
} from '@companion-professional/components';
import { useAuthStore } from '../../state/auth';
import { USER_SELECTABLE_LOCATION_TAGS } from '../../lib/config';
import { useCreateCheckupForPet } from '../../hooks/useCreateCheckupForPet';
import { CheckupLocationSelectionButtons } from '../CheckupDialogComponents';
import { CreateCheckupResults } from './CreateCheckupResults';

interface CreateCheckupForPetDialogProps {
  pet: PetAndClinicInfo;
}

// CreateCheckupForPetDialog is a dialog that allows the user to create a new checkup for a pet.  The user can select
// the location that the checkup will be preformed (in clinic or in home) by pressing one of the two buttons and the
// checkup will be created.
export const CreateCheckupForPetDialog: FunctionComponent<CreateCheckupForPetDialogProps> = ({ pet }) => {
  const { createCheckup, isPending, error, reset } = useCreateCheckupForPet();
  const [otherError, setOtherError] = useState<Error | null>(null);
  const [open, setOpen] = useState(false);
  const [checkupId, setCheckupId] = useState<string | null>(null);

  const handleCreateCheckup = useCallback(
    async (tag: CheckupTag) => {
      setOtherError(null);

      const clinicId = useAuthStore.getState().parsedToken?.clinic_id;
      if (!clinicId) {
        setOtherError(new Error('No clinic defined in the user token'));
        return;
      }
      const checkupId = await createCheckup({ clinicId, petId: pet.id, tags: [tag] });
      setCheckupId(checkupId);
    },
    [createCheckup, pet.id]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(openChange) => {
        reset();
        setCheckupId(null);
        setOpen(openChange);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="primary" size="small">
          <SquarePlus className="size-5" />
          &nbsp;Create Checkup for {pet.name}
        </Button>
      </DialogTrigger>
      {checkupId ? (
        <CreateCheckupResults checkupId={checkupId} />
      ) : (
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create a new checkup for {pet.name}</DialogTitle>
            <DialogDescription>
              Please select the location that this checkup will be preformed (i.e. the location that the video will be
              taken).
            </DialogDescription>
          </DialogHeader>
          <QueryLoaderWrapper isPending={isPending} error={error || otherError}>
            <CheckupLocationSelectionButtons
              onTagSelect={handleCreateCheckup}
              locationTags={USER_SELECTABLE_LOCATION_TAGS}
            />
          </QueryLoaderWrapper>
        </DialogContent>
      )}
    </Dialog>
  );
};
